import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/firebaseConfig';
import { useAuth } from '../firebase/useAuth';
import styles from './JoinScreen.module.css';

const onJoinShareFn = httpsCallable(functions, 'onJoinShare');

export default function JoinScreen({ shareId, user, onComplete, onCancel }) {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();

  const [ownerName, setOwnerName] = useState(null);
  const [shareError, setShareError] = useState(null);
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [authError, setAuthError] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  // Load share info (publicly readable — works before auth)
  useEffect(() => {
    getDoc(doc(db, 'shares', shareId)).then(snap => {
      if (!snap.exists()) {
        setShareError('This invite link is no longer valid.');
        return;
      }
      setOwnerName(snap.data().ownerName);
    }).catch(() => setShareError('Could not load invite.'));
  }, [shareId]);

  // If already signed in, call Cloud Function immediately
  useEffect(() => {
    if (!user || !ownerName) return;
    callJoin();
  }, [user, ownerName]); // eslint-disable-line

  const callJoin = async () => {
    setJoining(true);
    try {
      await onJoinShareFn({ shareId });
      onComplete();
    } catch (err) {
      if (err.code === 'functions/invalid-argument') {
        setJoinError("That's your own invite link.");
      } else {
        setJoinError(err.message || 'Something went wrong. Try again.');
      }
      setJoining(false);
    }
  };

  const handleGoogle = async () => {
    setAuthError('');
    try {
      await signInWithGoogle();
      // useEffect above fires after user state updates
    } catch (e) {
      setAuthError(e.message);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      if (mode === 'signup') {
        await signUpWithEmail(email, password, displayName);
      } else {
        await signInWithEmail(email, password);
      }
      // useEffect above fires after user state updates
    } catch (e) {
      setAuthError(e.message);
    }
  };

  if (shareError) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <p className={styles.error}>{shareError}</p>
          <button className={styles.cancelBtn} onClick={onCancel}>Go back</button>
        </div>
      </div>
    );
  }

  if (joining) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <p className={styles.joiningText}>Connecting you with {ownerName}…</p>
        </div>
      </div>
    );
  }

  if (joinError) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <p className={styles.error}>{joinError}</p>
          <button className={styles.cancelBtn} onClick={onCancel}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.inviteHeader}>
          <div className={styles.ownerAvatar}>
            {ownerName ? ownerName.charAt(0).toUpperCase() : '?'}
          </div>
          <h1 className={styles.title}>
            {ownerName ? `${ownerName} invited you` : 'Loading…'}
          </h1>
          <p className={styles.subtitle}>Share your daily creativity and see theirs.</p>
        </div>

        <button className={styles.googleBtn} onClick={handleGoogle}>
          Continue with Google
        </button>

        <div className={styles.divider}><span>or</span></div>

        <form onSubmit={handleEmailSubmit} className={styles.form}>
          {mode === 'signup' && (
            <input
              className={styles.input}
              type="text"
              placeholder="Your name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              required
            />
          )}
          <input
            className={styles.input}
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <input
            className={styles.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          {authError && <p className={styles.error}>{authError}</p>}
          <button type="submit" className={styles.submitBtn}>
            {mode === 'signup' ? 'Sign up & join' : 'Sign in & join'}
          </button>
        </form>

        <button className={styles.toggleMode} onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}>
          {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>

        <button className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
